import React, { useState, useCallback, useEffect, useRef } from 'react'
import {
  submitFormData,
  getPayers,
  filterPayers
} from '../../domain/benefits/verifyTxHelpers'
import { formatPhoneNumber } from '../../domain/benefits/benefitsProvider'
import { inputValidations } from '../../domain/benefits/VoBValidations'
import useSegment from '../../hooks/segment/useSegment'
import Ellipse from '../../images/Ellipse'
import { PAYERS_FALLBACK_LIST } from '../../domain/benefits/constants'

const formGroupStyle =
  'w-full mx-auto flex flex-col items-start gap-2 focus-within:border-primary-300'
const formFieldStyle =
  'focus:border-primary-300 border-gray-300 focus-within:ring-primary-300 active:border-primary-300 placeholder:text-gray-600 text-sm rounded-lg block w-full'
const labelStyle = 'text-sm text-gray-1100'
const requiredStyle = 'inline-flex text-sm text-gray-800'

const VoBForm = ({
  benefitsKey,
  benefitsProvider = 'verifyTx',
  onClose,
  centerTitle,
  description = ''
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    email: '',
    phone: '',
    insuranceCompany: '',
    payerId: '',
    memberId: '',
    additionalNotes: ''
  })
  const [formIsSubmitting, setFormIsSubmitting] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [hasFromSubmitSucceeded, setHasFromSubmitSucceeded] = useState(false)
  const [insurerList, setInsurerList] = useState(PAYERS_FALLBACK_LIST)
  const [filteredInsurerList, setFilteredInsurerList] =
    useState(PAYERS_FALLBACK_LIST)
  const [selectedInsurer, setSelectedInsurer] = useState(null)
  const [validationErrors, setValidationErrors] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    email: '',
    phone: '',
    insuranceCompany: '',
    memberId: '',
    additionalNotes: ''
  })
  const [showInsuranceSelector, setShowInsuranceSelector] = useState(false)

  useEffect(() => {
    async function getPayerList(benefitsKey) {
      const payersList = await getPayers(benefitsKey)
      setInsurerList(payersList)
    }
    getPayerList(benefitsKey)
  }, [benefitsKey])

  const validateInput = useCallback(
    (e) => {
      const { name, value } = e.target
      const updatedValidations = inputValidations(name, value, formData)
      setValidationErrors({ ...validationErrors, ...updatedValidations })
    },
    [formData, validationErrors]
  )

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  //TODO: add debounce to avoid multiple calls to filterPayers
  const handleInsuranceChange = (e) => {
    setFormData({ ...formData, insuranceCompany: e.target.value })

    if (e.target.value !== selectedInsurer?.payerName) {
      setSelectedInsurer(null)
      setFormData({
        ...formData,
        insuranceCompany: e.target.value,
        payerId: ''
      })
    }

    setFilteredInsurerList(filterPayers(e.target.value, insurerList))
  }

  const onBlurInsuranceChange = (e) => {
    const event = e
    validateInput(event)
    setTimeout(() => {
      setShowInsuranceSelector(false)
    }, 100)
  }

  const handleSetInsurer = (payerId, payerName) => {
    setSelectedInsurer({ payerId, payerName })
    setFormData({
      ...formData,
      insuranceCompany: payerName,
      payerId: payerId
    })

    setValidationErrors({
      ...validationErrors,
      insuranceCompany: null,
      payerId: null
    })
    setShowInsuranceSelector(false)
  }

  const segment = useSegment()

  const handleSubmit = async () => {
    setFormIsSubmitting(true)
    const requiredFields = [
      'firstName',
      'lastName',
      'dob',
      'email',
      'phone',
      'insuranceCompany',
      'payerId',
      'memberId'
    ]

    if (
      Object.values(validationErrors).some((field) => field) ||
      !Object.values(formData)
        .slice(0, -1)
        .every((field) => field)
    ) {
      const updatedValidations = {}
      requiredFields.forEach((field) => {
        if (!formData[field] && !validationErrors[field]) {
          updatedValidations[field] = 'This field is required.'
        }
      })
      setValidationErrors({ ...validationErrors, ...updatedValidations })
      setFormIsSubmitting(false)
      return
    }

    try {
      const submitFormResponse = await submitFormData({
        form: formData,
        benefitsKey,
        benefitsProvider
      })

      setHasFromSubmitSucceeded(submitFormResponse)
      if (submitFormResponse) {
        segment.verifyBenefits.submitted({ center: centerTitle })
      }
    } catch (error) {
      console.log('ERROR submitting VoB form', error)
    } finally {
      setFormSubmitted(true)
      setFormIsSubmitting(false)
    }
  }

  if (formSubmitted) {
    return (
      <div className="w-full h-full md:h-auto p-6 flex items-center justify-start bg-white">
        <div className="w-full h-full md:h-auto md:min-h-[280px] flex flex-col items-center justify-start">
          <div className="text-2xl font-semibold text-center pb-4 text-gray-900">
            {hasFromSubmitSucceeded
              ? `Success! ${centerTitle}’s admission team will review your information and contact you in a follow-up call.`
              : "We're sorry, there was an error submitting your request. Please try again later."}
          </div>
          <div className="button-col">
            <button
              type="close"
              className="bg-primary-500 text-white px-3 py-2 rounded-lg disabled:bg-gray-300 hover:bg-primary-600"
              onClick={onClose}
            >
              Back To Center
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-auto sm:w-full ">
      <div className="text-sm text-gray-1100 leading-6 mx-auto">
        {`${centerTitle} uses this information to verify your insurance and determine potential coverage.`}
      </div>
      <div className="text-sm text-gray-1100 leading-6 mx-auto mb-6">
        {`As an independent resource, Recovery.com does not verify insurance or coverage. Direct all inquiries and questions to ${centerTitle}.`}
      </div>

      <form
        onSubmit={(e) => e.preventDefault()}
        autoComplete="on"
        name="benefits-form"
        className="space-y-4 w-full h-full sm:mx-auto flex flex-col items-center justify-start pb-6"
      >
        <div className={`sele ${formGroupStyle}`}>
          <label className={`${labelStyle}`}>
            First Name <span className={`${requiredStyle}`}>(Required)</span>
          </label>
          <input
            name="firstName"
            value={formData.firstName}
            aria-label="first name"
            title="Enter your first name"
            onChange={handleChange}
            onBlur={validateInput}
            placeholder="First Name"
            className={`${formFieldStyle} ${
              validationErrors?.firstName ? 'border-red-300' : undefined
            }`}
          />
          <span
            className={`${
              validationErrors?.firstName ? 'flex' : 'hidden'
            } text-red-300 text-sm`}
          >
            {validationErrors?.firstName}
          </span>
        </div>
        <div className={`last-name-input ${formGroupStyle}`}>
          <label className={`${labelStyle}`}>
            Last Name <span className={`${requiredStyle}`}>(Required)</span>
          </label>
          <input
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            onBlur={validateInput}
            placeholder="Last Name"
            aria-label="last name"
            title="Enter your last name"
            className={`${formFieldStyle} ${
              validationErrors?.lastName ? 'border-red-300' : undefined
            }`}
          />
          <span
            className={`${
              validationErrors?.lastName ? 'flex' : 'hidden'
            } text-red-300 text-sm`}
          >
            {validationErrors?.lastName}
          </span>
        </div>

        <div className={`${formGroupStyle} my-2`}>
          <label className={`${labelStyle}`}>
            Date of Birth <span className={`${requiredStyle}`}>(Required)</span>
          </label>{' '}
          <input
            name="dob"
            value={formData.dob}
            onChange={handleChange}
            onSelect={handleChange}
            onBlur={validateInput}
            placeholder="Date of Birth"
            title="Enter your date of birth"
            aria-label="date of birth"
            type="date"
            className={`${formFieldStyle} flex ${
              validationErrors?.dob ? 'border-red-300' : undefined
            }`}
          />
          <span
            className={`${
              validationErrors.dob ? 'flex' : 'hidden'
            } text-red-300 text-sm`}
          >
            {validationErrors?.dob}{' '}
          </span>
        </div>
        <div className={`${formGroupStyle}`}>
          <label
            className={`${labelStyle}`}
            aria-label="email"
          >
            Email Address <span className={`${requiredStyle}`}>(Required)</span>
          </label>
          <input
            name="email"
            value={formData.email}
            aria-label="email"
            title="Enter your email address"
            onChange={handleChange}
            onBlur={validateInput}
            placeholder="Email"
            className={`${formFieldStyle} ${
              validationErrors?.email ? 'border-red-300' : undefined
            }`}
          />
          <span
            className={`${
              validationErrors?.email ? 'flex' : 'hidden'
            } text-red-300 text-sm`}
          >
            {validationErrors?.email}{' '}
          </span>
        </div>
        <div className={`phone-input ${formGroupStyle}`}>
          <label className={`${labelStyle}`}>
            Phone Number <span className={`${requiredStyle}`}>(Required)</span>
          </label>
          <input
            name="phone"
            value={formatPhoneNumber(formData.phone)}
            onChange={handleChange}
            onBlur={validateInput}
            placeholder="Phone Number"
            title="Enter your phone number"
            aria-label="phone number"
            type="tel"
            minLength={10}
            className={`${formFieldStyle} ${
              validationErrors?.phone ? 'border-red-300' : undefined
            }`}
          ></input>
          <span
            className={`${
              validationErrors?.phone ? 'flex' : 'hidden'
            } text-red-300  text-sm`}
          >
            {validationErrors?.phone}{' '}
          </span>
        </div>
        <div className={`insurerList-input ${formGroupStyle}`}>
          <label className={`${labelStyle}`}>
            Insurance Provider{' '}
            <span className={`${requiredStyle}`}>(Required)</span>
          </label>{' '}
          <input
            name="insuranceCompany"
            value={formData.insuranceCompany}
            aria-label="insurance provider"
            title="Select your insurance provider"
            onChange={handleInsuranceChange}
            placeholder="Search for your insurance provider"
            className={`${formFieldStyle} first:active:visible ${
              validationErrors?.insuranceCompany ? 'border-red-300' : undefined
            }`}
            onFocus={() => setShowInsuranceSelector(true)}
            onBlur={(e) => onBlurInsuranceChange(e)}
          />
          {showInsuranceSelector && (
            <div
              className={`relative h-[200px] w-full border-gray-1000 overflow-x sm:mt-[-8px] `}
            >
              {insurerList.length === 0 ? (
                <ul className="absolute z-20 h-full w-auto sm:w-full gap-y-2 flex flex-col bg-white border border-gray-300 overflow-y-scroll rounded-md shadow-lg first:pt-2">
                  <li className="flex row sm:pl-2 text-gray-700 mx-auto">
                    No results found
                  </li>
                </ul>
              ) : (
                <ul className="absolute px-2 z-20 h-full w-auto sm:w-full gap-y-2 flex flex-col bg-white border border-gray-300 overflow-y-scroll rounded-md shadow-lg first:pt-2">
                  {filteredInsurerList.map((item, index) => {
                    return (
                      <li
                        className={`flex row cursor-pointer rounded-lg hover:bg-primary-50 px-2 ${
                          item.payerId === selectedInsurer?.payerId
                            ? 'bg-primary-100'
                            : ''
                        }`}
                        key={index}
                        itemID={item.payerId}
                        aria-label={item.payerName}
                        title={item.payerName}
                        onClick={() => {
                          handleSetInsurer(item.payerId, item.payerName)
                        }}
                        onSelect={() => {
                          handleSetInsurer(item.payerId, item.payerName)
                        }}
                      >
                        {item.payerName}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )}
          <span
            className={`${
              validationErrors?.insuranceCompany ? 'flex' : 'hidden'
            } text-red-300  text-sm`}
          >
            {validationErrors.insuranceCompany}{' '}
          </span>
        </div>
        <div className={`${formGroupStyle}`}>
          <label className={`${labelStyle}`}>
            Member ID <span className={`${requiredStyle}`}>(Required)</span>
          </label>
          <input
            name="memberId"
            value={formData.memberId}
            aria-invalid={validationErrors.memberId}
            aria-label="member id"
            onChange={handleChange}
            onBlur={validateInput}
            placeholder="Member ID"
            className={`${formFieldStyle} ${
              validationErrors?.memberId ? 'border-red-300' : undefined
            }`}
          />
          <span
            className={`${
              validationErrors?.memberId ? 'flex' : 'hidden'
            } text-red-300  text-sm`}
          >
            {validationErrors?.memberId}{' '}
          </span>
        </div>
        <br />
        <div className="w-full items-start pt-2">
          <button
            type="submit"
            className="bg-primary-500 text-white px-3 py-2 rounded-lg disabled:bg-gray-300"
            onClick={handleSubmit}
            disabled={formIsSubmitting}
          >
            {formIsSubmitting ? (
              <Ellipse className="animate-spin mx-3.5" />
            ) : (
              <span>Submit</span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default VoBForm
